/**
 * MaintControl — Entry Point
 *
 * Imports all modules and wires navigation, init, and window-level bindings.
 */

declare global {
    interface Window {
        [key: string]: any;
    }
}

// ─────────────────────────────────────────────────────────────────────────
// Fix 10 mai 2026 — sesiune expirată VIZIBILĂ
//
// Înainte: când API-urile returnează 401 silențios (ex: /api/operator_tasks
// la „Lucrările mele"), pagina rămâne cu spinner blocat „Se încarcă..." la
// nesfârșit. Utilizatorul nu știe că s-a delogat. Trebuia să navigheze
// înapoi în Hub și să revină ca să apară login-ul.
//
// Acum: orice fetch către `/api/...` care primește 401 declanșează
// IMEDIAT un overlay opac peste tot UI-ul + redirect:
//   - În embed mode → postMessage('reauth_required') la Hub. Hub face
//     logout()+reload, utilizatorul vede ecranul de login (poza 2).
//   - Standalone → location.assign('/?logout=1') → login Mentenanță.
//
// Patch 10 mai 2026 noaptea — anti-loop:
// Pe `/?logout=1` (sau cu auth-gate vizibil) NU mai facem redirect. Niște
// IIFE-uri / inline scripts (chat-ai _initChatMachineSelector, recon
// loadJobsList) trag API-uri ÎNAINTE ca initApp() să confirme auth. La
// userul nelogat primesc 401 → vechiul handler făcea redirect la
// /?logout=1 → buclă infinită (overlay ↔ login form).
// Acum, dacă suntem deja pe pagina de login, înghițim 401-ul fără overlay.
// ─────────────────────────────────────────────────────────────────────────
(function installAuthExpiredInterceptor(){
    let handled = false;

    // Anti-loop guard: detect that the auth gate is already on screen.
    // Conditions that mean "user not logged in yet, login form is the active UI":
    //   - URL has `?logout=1` (just logged out) or `?idle=...` (idle timeout)
    //   - #auth-gate exists and is visible (not hidden by class/style)
    //   - #app-wrapper has `auth-locked` class
    // In any of those cases, a 401 from a background fetch is EXPECTED — we
    // just swallow it. Redirecting again would loop forever.
    function isOnLoginPage(): boolean {
        try {
            const qs = window.location.search || '';
            if (qs.indexOf('logout=1') !== -1) return true;
            if (qs.indexOf('idle=') !== -1) return true;
            const gate = document.getElementById('auth-gate');
            if (gate) {
                if (gate.classList.contains('hidden')) {
                    // explicit hidden → gate not active
                } else {
                    // Computed style check — covers both display:none and
                    // visibility:hidden set inline or via CSS.
                    const cs = (typeof window.getComputedStyle === 'function')
                        ? window.getComputedStyle(gate)
                        : null;
                    if (!cs) return true; // can't tell → assume gate is up (safe)
                    if (cs.display !== 'none' && cs.visibility !== 'hidden') return true;
                }
            }
            const appWrap = document.getElementById('app-wrapper');
            if (appWrap && appWrap.classList.contains('auth-locked')) return true;
        } catch (_) { /* DOM not ready or hostile env — be conservative */ }
        return false;
    }

    function showOverlay(): void {
        try {
            if (document.getElementById('__auth_expired_overlay__')) return;
            const o = document.createElement('div');
            o.id = '__auth_expired_overlay__';
            o.style.cssText = [
                'position:fixed','inset:0','z-index:2147483647',
                'background:#0a0f1c','color:#cbd5e1',
                'display:flex','align-items:center','justify-content:center',
                'font-family:system-ui,-apple-system,sans-serif',
                'animation:authExpFadeIn .15s ease-out',
            ].join(';');
            o.innerHTML =
                '<style>'
                + '@keyframes authExpFadeIn{from{opacity:0}to{opacity:1}}'
                + '@keyframes authExpSpin{to{transform:rotate(360deg)}}'
                + '</style>'
                + '<div style="text-align:center;padding:24px;max-width:360px">'
                + '<div style="width:40px;height:40px;border:3px solid rgba(0,212,255,.25);'
                + 'border-top-color:#00d4ff;border-radius:50%;'
                + 'animation:authExpSpin 1s linear infinite;margin:0 auto 18px"></div>'
                + '<h2 style="color:#00d4ff;font-family:Orbitron,monospace;'
                + 'font-size:1.05rem;margin:0 0 8px;letter-spacing:.04em">'
                + 'SESIUNE EXPIRATĂ</h2>'
                + '<p style="color:#94a3b8;font-size:.9rem;margin:0 0 4px">'
                + 'Re-autentificare necesară</p>'
                + '<p style="color:#64748b;font-size:.78rem;margin:0">'
                + 'Te redirectăm la pagina de login...</p>'
                + '</div>';
            document.body.appendChild(o);
        } catch (_) { /* DOM nepregătit — ignor */ }
    }

    function triggerAuthExpired(): void {
        if (handled) return;
        // Anti-loop: already on the login screen — the 401 is expected
        // (background fetches running before auth handshake). Swallow it and
        // mark handled so we don't keep re-checking the DOM on every 401.
        if (isOnLoginPage()) {
            handled = true;
            return;
        }
        handled = true;
        const inEmbed = !!(window as any).__EMBED_HUB__;
        showOverlay();
        if (inEmbed) {
            // Hub primește mesajul, face logout()+reload → utilizatorul vede login.
            try {
                if (window.parent && window.parent !== window) {
                    window.parent.postMessage(
                        { type: 'reauth_required', from: 'mentenanta', reason: 'api_401' },
                        'https://hub.cpp-romania.com'
                    );
                }
            } catch (_) { /* parent inaccessible */ }
            // Fallback hard: dacă Hub nu reacționează în 4s, navigăm noi în iframe.
            setTimeout(() => {
                try { (window.top || window).location.assign('https://hub.cpp-romania.com/'); }
                catch (_) { window.location.assign('/?logout=1'); }
            }, 4000);
        } else {
            // Standalone: redirect direct la pagina de login (auth-gate apare).
            setTimeout(() => { window.location.assign('/?logout=1'); }, 250);
        }
    }

    const originalFetch = window.fetch.bind(window);
    window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        let response: Response;
        try {
            response = await originalFetch(input as any, init);
        } catch (err) {
            throw err; // network error — propagă (nu confunda cu auth)
        }
        if (response.status === 401 && !handled) {
            // Doar pentru endpoint-uri API (nu HTML), și doar dacă e cu adevărat
            // o expirare de sesiune (auth_required sau idle_timeout).
            try {
                const url = typeof input === 'string' ? input
                          : (input as Request)?.url || (input as URL)?.toString?.() || '';
                // Exempt auth-check endpoints: their 401 is the protocol itself,
                // not a session expiration. Without this, /api/auth/me 401 (after
                // logout, before showing login form) would trigger the overlay.
                const isAuthEndpoint = (
                    url.indexOf('/api/auth/me') !== -1
                    || url.indexOf('/api/auth/login') !== -1
                    || url.indexOf('/api/auth/logout') !== -1
                    || url.indexOf('/api/auth/usernames') !== -1
                );
                if (url.indexOf('/api/') !== -1 && !isAuthEndpoint) {
                    // Best-effort: încerc să citesc body JSON, dar fără să
                    // consum response-ul original (clone).
                    const cl = response.clone();
                    let isAuthExpire = false;
                    try {
                        const data = await cl.json();
                        if (data && (data.auth_required || data.idle_timeout)) {
                            isAuthExpire = true;
                        }
                    } catch (_) {
                        // Body non-JSON → presupun că 401 generic înseamnă tot auth expirat.
                        isAuthExpire = true;
                    }
                    if (isAuthExpire) triggerAuthExpired();
                }
            } catch (_) { /* swallowed — nu blochez fetch-ul pentru handling */ }
        }
        return response;
    };
})();

import { dBaraJosLucrari } from './tema';
import { initManualLaPagina } from './manual-overlay';
import { initAtmosphereCanvas } from './atmosphere';
import { initSpatialNavDemo, openSpatialNavDemo } from './spatial-nav-demo';
import { registerShowSection } from './nav-bridge';
import { setRouteHash, parseRouteHash } from './deep-link';
import { applyInitialAppRoute } from './app-route';

import {
    state, api, $, $$, toast, escHtml, truncate, todayStr, localDateKey,
    _timeAgo, recordHoursHistory, buildSparklineSVG,
    computeAlerts, updateAlarmBadge, renderAlarmPanel,
    _updateHelpBadge, _openHelpDashboard,
    _maintIsLayerVisible, _maintCloseTopLayer, _initMaintHistoryBack,
    loadAlarmHistory,
    openAlarmHistoryForLine, openAlarmHistoryForMachine,
    _ensureMachineCache, _allMachinesCache, _lineMap, _lineLabels,
    _buildLineMap, _updateMachineSelect, _populateLineSelect,
    _linkLineToMachineSelect, _insertLineSelectBefore, setAllMachinesCache,
    incarcaIncidenteAplicatie, actualizeazaButonAlarme,
} from './core';

import {
    openLineDetail, renderDetailTab, detailTabActive,
} from './line-detail';

import { openLineSummary } from './line-summary';
import { askText, askConfirm, askChoice } from './dialogs';

import {
    loadCurrentUser, _syncAiModeButtons, _checkAiLocalStatus,
    _checkAiCloudStatus, _checkCollectorStatus,
    _fetchProactiveAlerts, _closeProactivePanel,
    loadUsersModal, _checkClarificationNotifications,
    _buildTreeSectionOptions, _renderDocumentTemplateNavigator,
    _renderTreeView, _renderTreeNode,
    _renderDetectedChaptersList, _renderCategoriesView,
    refreshDocumentProcessingStatus, ensureReportsInNav,
} from './auth-ui';

import {
    openRevisionDetailView, closeRevisionDetailView,
    _tryResumeWorkflow, _clearWorkflowSession,
    _wfWorkflowId, _wfSetStep, _wfRenderStep1,
    ensureRevisionDetailOverlay,
} from './workflow';

import {
    loadSensorConfigs, refreshSensorReadingsFromServer, refreshHourlyAvg,
    getHourlyAvgPower, getLineAttributedPower, getSensorValue, sensorRnd, generateSensors,
    getSensorSeverity, getEquipSeverity,
    loadMoldCounters, refreshMoldCountersFromServer, updateMoldCounters,
    saveMoldCountersToServer, updateSensors, updateSensorDisplays,
    _refreshDetailHeader, equipmentTypeLabel, buildGaugeSVG, buildEquipCard,
    _applyRoleMenus, loadDashboard, _dashUrgent, _dashMyTasks, _dashAskAI, loadOverview,
    loadGoliveStatus,
    refreshControllerAlarms, _playAlarmSound,
    openLineLiveDetail,
    TYPE_LABELS,
} from './overview';

import {
    loadFactoryLayout, _layoutState,
    _layoutDeletePhoto, _layoutViewPhoto,
    _layoutUpdateEq, _layoutUpdateAnnot,
} from './layout';

import {
    loadMachineTypes, populateTypeSelect,
    openAddLineModal, openAddEquipModal,
    openSharedSensorsModal, openCountersModal, openCollectorModal,
    openEditEquipModal, _compressImage,
    _initLabelScan, _initPhotoUpload, _loadMachinePhotos,
    buildSensorConfigRow, getSensorConfigsFromContainer, getSensorConfigsFromForm,
    buildLinesCheckboxes, getCheckedLines,
    PROTOCOL_LABELS, CONNECTION_PROTOCOL_OPTIONS,
} from './config';

import {
    loadMyTasks, _buildDateStrip, _renderMyTasks, loadCalendar,
    ensureCalendarStructure, buildCalendarEvents,
    renderCalendarGrid, renderCalendarTable,
    openCalDayPanel, goToOverviewDetail,
    _openAssignDialog, _openNewProcedureForm, openModal,
    HOURS_PER_DAY,
} from './tasks-calendar';

import {
    initParts, loadParts, _startArCamera, _stopArCamera,
    _openLightbox, _attachSourceClickHandlers,
    _loadPartsPhotosGallery, _loadSchemes,
    initErrors, loadErrors, _initErrorsInDiag,
    initExplorer,
} from './parts';

import { loadPartsOrder } from './parts-order';

import {
    loadJournal, loadJournalLog, urgShowPhase, urgUpdateProgress,
    _getMachineLineId, _intvAddFiles, _intvRenderPreview,
    _loadSimilarInterventions, _loadAISuggestions,
    _aiAnswerIsEmpty, _buildCloudBtn, _loadAIReferencedDocs,
    _showActiveInterventionBanner,
} from './journal';

import {
    loadReports, loadReportsData, openLogDetail,
    _loadWorkflowReports, openWorkflowDetail, loadMonthlyReport, loadBriefingProposals,
    renderPagination, _show3DUploadDialog,
    _delete3DModel,
} from './reports';

import {
    _renderEnergyMonitor, _emLoadHistory, _emRenderChart,
    _updateEnergyLiveValues, destroyChart,
    loadStatistics, makeBarChart, makeDoughnutChart, makeStackedBar,
    EM_PARAMS, EM_PERIODS, chartInstances, CHART_COLORS,
} from './energy-stats';

import {
    ensureChatApiSetup, ensureChatApiStatusLine, initChat,
    _updateSmartSuggestions, _createFeedbackHtml,
    _globalFbVote, _globalFbCorrect,
    _loadChatHistory, sendChatMessage, addChatMsg,
    renderMarkdown, buildMarkdownTable,
    _chReview, _chReviewWithCorrection,
} from './chat-ai';

import {
    initDiagnostic, runDiagnostic, _diagEscalate, openDiagManualFromAiResult, activateDiagnosticTab, loadDiagManualDocumentsList,
    loadDiagnosticLessons, _loadPendingLessonsCount, _openLessonEditor,
    _fmtTimer, openProcedureOverlay, closeProcOverlay,
    SYMPTOM_DB,
} from './diagnostic';

import { initManuals } from './manuals';

import {
    openProductQuality, _loadQualityAlerts,
    _pqRenderSpec, _pqRenderImages, _pqRenderVideo, _pqRenderCheck,
    _pqUpload, _pqDeleteDoc, _pqDeleteTemplate, _pqSubmitCheck,
    _pqCreateTemplate, _pqAddField, _pqCheckValue,
} from './quality';

import {
    openLineLayoutDetail, _renderLLLayout, _renderLLElements, _renderLLMedia,
} from './line-layout';

import {
    loadFactoryConfig, getFactoryConfig, isFeatureEnabled, applyFactoryTheme,
} from './factory-config';

import {
    loadProceduresEditor, loadValidationInbox, updateValidationBadge,
} from './procedures';

import {
    loadArView, destroyArView,
    _arConfirmIdentification, _arCorrectIdentification,
    _arOpenDetail, _arStartMaintenance, _arIdentifyPart,
    _arUploadPhoto, _arToggleTraining, _arStartYoloTraining,
} from './ar-view';

import { initGoldenSet } from './golden-set';
import { initSistemSanatate , actualizeazaInsignaIncidente } from './sistem-sanatate';
import { initConfirmari } from './confirmari';
import { initGhid, ataseazaAjutor } from './ghid';
import './machine-panel'; // B3: panou utilaj (self-register window.openMachinePanel)

import {
    openLightbox as openMachineLightbox,
    closeLightbox as closeMachineLightbox,
    fetchMachinePhotos, fetchMachinePhotosBatch,
    getCachedMachinePhotos, invalidateMachinePhotos,
    buildPhotoThumbHTML, attachPhotoThumbHandlers,
} from './lightbox';

import {
    loadCollectorsAdmin,
    _collectorsRotateKey,
    _collectorsSaveGating,
    _collectorsMarkDirty,
    _collectorsBuildBundle,
    loadLinesStatusCard,
    startLinesStatusAutoRefresh,
} from './collectors-admin';

import {
    loadReconstruction, destroyReconstruction,
    _reconCreate, _reconFilesSelected, _reconStart, _reconViewJob,
} from './reconstruction';

/* ═══ EXPOSE TO WINDOW (for inline HTML handlers) ════════════ */
Object.assign(window, {
    // Core
    state, api, $, $$, toast, escHtml, truncate, todayStr, localDateKey,
    _timeAgo, recordHoursHistory, buildSparklineSVG,
    computeAlerts, updateAlarmBadge, renderAlarmPanel,
    _updateHelpBadge, _openHelpDashboard,
    _maintIsLayerVisible, _maintCloseTopLayer, _initMaintHistoryBack,
    loadAlarmHistory,
    openAlarmHistoryForLine, openAlarmHistoryForMachine,
    _ensureMachineCache, _allMachinesCache, _lineMap, _lineLabels,
    _buildLineMap, _updateMachineSelect, _populateLineSelect,
    _linkLineToMachineSelect, _insertLineSelectBefore, setAllMachinesCache,

    // Line detail
    openLineDetail, renderDetailTab, detailTabActive,

    // Line summary modal (compact, click on line header)
    openLineSummary,

    // Photo lightbox (galerie poze utilaj)
    openMachineLightbox, closeMachineLightbox,
    fetchMachinePhotos, fetchMachinePhotosBatch,
    getCachedMachinePhotos, invalidateMachinePhotos,
    buildPhotoThumbHTML, attachPhotoThumbHandlers,

    // Auth UI
    loadCurrentUser, _syncAiModeButtons, _checkAiLocalStatus,
    _checkAiCloudStatus, _checkCollectorStatus,
    _fetchProactiveAlerts, _closeProactivePanel,
    loadUsersModal, _checkClarificationNotifications,
    _buildTreeSectionOptions, _renderDocumentTemplateNavigator,
    _renderTreeView, _renderTreeNode,
    _renderDetectedChaptersList, _renderCategoriesView,
    refreshDocumentProcessingStatus, ensureReportsInNav,

    // Workflow
    openRevisionDetailView, closeRevisionDetailView,
    _tryResumeWorkflow, _clearWorkflowSession,
    _wfWorkflowId, _wfSetStep, _wfRenderStep1,
    ensureRevisionDetailOverlay,

    // Overview / Sensors
    loadSensorConfigs, refreshSensorReadingsFromServer, refreshHourlyAvg,
    getHourlyAvgPower, getLineAttributedPower, getSensorValue, sensorRnd, generateSensors,
    getSensorSeverity, getEquipSeverity,
    loadMoldCounters, refreshMoldCountersFromServer, updateMoldCounters,
    saveMoldCountersToServer, updateSensors, updateSensorDisplays,
    _refreshDetailHeader, equipmentTypeLabel, buildGaugeSVG, buildEquipCard,
    _applyRoleMenus, loadDashboard, _dashUrgent, _dashMyTasks, _dashAskAI, loadOverview,
    loadGoliveStatus,
    refreshControllerAlarms, _playAlarmSound, openLineLiveDetail, TYPE_LABELS,

    // Layout
    loadFactoryLayout, _layoutState,
    // Inline-handler bindings for the hall layout: gallery thumbnails use
    // onclick="_layoutDeletePhoto(id)" / onclick="_layoutViewPhoto(url)" and
    // the edit panel uses onchange="_layoutUpdateEq(...)" / _layoutUpdateAnnot.
    // Without these explicit window exposures the IIFE-bundled functions are
    // not reachable from inline HTML handlers — bug 2026-05-14 (gallery X did nothing).
    _layoutDeletePhoto, _layoutViewPhoto,
    _layoutUpdateEq, _layoutUpdateAnnot,

    // Config
    loadMachineTypes, populateTypeSelect,
    openAddLineModal, openAddEquipModal,
    openSharedSensorsModal, openCountersModal, openCollectorModal,
    openEditEquipModal, _compressImage,
    _initLabelScan, _initPhotoUpload, _loadMachinePhotos,
    buildSensorConfigRow, getSensorConfigsFromContainer, getSensorConfigsFromForm,
    buildLinesCheckboxes, getCheckedLines,
    PROTOCOL_LABELS, CONNECTION_PROTOCOL_OPTIONS,

    // Tasks / Calendar
    loadMyTasks, _buildDateStrip, _renderMyTasks, loadCalendar,
    ensureCalendarStructure, buildCalendarEvents,
    renderCalendarGrid, renderCalendarTable,
    openCalDayPanel, goToOverviewDetail,
    _openAssignDialog, _openNewProcedureForm, openModal,
    HOURS_PER_DAY,

    // Parts
    initParts, loadParts, _startArCamera, _stopArCamera,
    _openLightbox, _attachSourceClickHandlers,
    _loadPartsPhotosGallery, _loadSchemes,
    initErrors, loadErrors, _initErrorsInDiag,
    initExplorer,
    loadPartsOrder,

    // Journal
    loadJournal, loadJournalLog, urgShowPhase, urgUpdateProgress,
    _getMachineLineId, _intvAddFiles, _intvRenderPreview,
    _loadSimilarInterventions, _loadAISuggestions,
    _aiAnswerIsEmpty, _buildCloudBtn, _loadAIReferencedDocs,

    // Reports
    loadReports, loadReportsData, openLogDetail,
    _loadWorkflowReports, openWorkflowDetail, loadMonthlyReport, loadBriefingProposals,
    renderPagination, _show3DUploadDialog,
    // Inline-handler binding: 3D models table uses onclick="_delete3DModel(id)".
    // Same pattern fix as for gallery — without this, the X button silently fails.
    _delete3DModel,

    // Energy / Stats
    _renderEnergyMonitor, _emLoadHistory, _emRenderChart,
    _updateEnergyLiveValues, destroyChart,
    loadStatistics, makeBarChart, makeDoughnutChart, makeStackedBar,
    EM_PARAMS, EM_PERIODS, chartInstances, CHART_COLORS,

    // Chat
    ensureChatApiSetup, ensureChatApiStatusLine, initChat,
    _updateSmartSuggestions, _createFeedbackHtml,
    _globalFbVote, _globalFbCorrect,
    _loadChatHistory, sendChatMessage, addChatMsg,
    renderMarkdown, buildMarkdownTable,
    // Inline-handler bindings for chat-history review buttons (👍/👎/✏️).
    _chReview, _chReviewWithCorrection,

    // Diagnostic / Procedures
    initDiagnostic, runDiagnostic, _diagEscalate, openDiagManualFromAiResult, activateDiagnosticTab, loadDiagManualDocumentsList,
    loadDiagnosticLessons, _loadPendingLessonsCount, _openLessonEditor,
    _fmtTimer, openProcedureOverlay, closeProcOverlay, SYMPTOM_DB,

    // Quality
    openProductQuality, _loadQualityAlerts,

    // Line Layout
    openLineLayoutDetail,

    // Factory Config
    loadFactoryConfig, getFactoryConfig, isFeatureEnabled, applyFactoryTheme,

    // Procedures & Validation
    loadProceduresEditor, loadValidationInbox, updateValidationBadge,

    // AR View
    loadArView, destroyArView,
    _arConfirmIdentification, _arCorrectIdentification,
    _arOpenDetail, _arStartMaintenance, _arIdentifyPart,
    _arUploadPhoto, _arToggleTraining, _arStartYoloTraining,

    // Golden Set
    initGoldenSet,

    // 3D Reconstruction
    loadReconstruction, destroyReconstruction,
    _reconCreate, _reconFilesSelected, _reconStart, _reconViewJob,

    // Collectors admin (Etapa 2 colector)
    loadCollectorsAdmin,
    _collectorsRotateKey,
    _collectorsSaveGating,
    _collectorsMarkDirty,
    _collectorsBuildBundle,
    loadLinesStatusCard,
    startLinesStatusAutoRefresh,

    // Navigation
    showSection,
});

/* ═══ NAVIGATION ═══════════════════════════════════════════════ */
function runSectionLoaders(section: string | undefined): void {
    if (!section) return;
    // camera AR se oprește când pleci din secțiune (30 sep 2026: rămânea pornită, cu cereri în fundal)
    if (section !== 'ar') destroyArView();
    if (section === 'dashboard') loadDashboard();
    if (section === 'mytasks') loadMyTasks();
    if (section === 'overview') loadOverview();
    if (section === 'calendar') {
        loadCalendar();
        setTimeout(_tryResumeWorkflow, 400);
    }
    if (section === 'parts') {
        initParts();
        initExplorer();
    }
    if (section === 'parts-order') loadPartsOrder();
    if (section === 'journal') loadJournal();
    if (section === 'reports') loadReports();
    if (section === 'statistics') loadStatistics();
    if (section === 'chat') initChat();
    if (section === 'diagnostic') initDiagnostic();
    if (section === 'manuals') void initManuals();
    if (section === 'procedures') loadProceduresEditor();
    if (section === 'validation') loadValidationInbox();
    if (section === 'confirmari') initConfirmari();
    if (section === 'alarm_history') loadAlarmHistory();
    if (section === 'schedule-intervention') initScheduleIntervention();
    if (section === 'ar') loadArView();
    if (section === 'golden-set') initGoldenSet();
    if (section === 'sistem-sanatate') initSistemSanatate();
    if (section === 'reconstruction') loadReconstruction();
    if (section === 'ghid') void initGhid();
    // butonul „Ajutor” în capul secțiunii (31 oct 2026); a doua încercare pentru secțiunile care se desenează mai târziu
    setTimeout(() => ataseazaAjutor(section), 300);
    setTimeout(() => ataseazaAjutor(section), 1500);
}

function showSection(secId: string): void {
    const sectionName = secId.replace(/^sec-/, '');
    const link = document.querySelector(`.nav-links a[data-section="${sectionName}"]`);
    if (link) {
        (link as HTMLElement).click();
        return;
    }
    const target = document.getElementById(secId);
    if (!target) return;
    $$('.nav-links a').forEach(x => x.classList.remove('active'));
    const navMatch = document.querySelector(`.nav-links a[data-section="${sectionName}"]`);
    if (navMatch) navMatch.classList.add('active');
    const altaSectiune = !target.classList.contains('active');
    $$('.section').forEach(s => s.classList.remove('active'));
    target.classList.add('active');
    if (altaSectiune) window.scrollTo(0, 0);
    const titleEl = navMatch?.textContent?.trim() || sectionName;
    $('#page-title')!.textContent = titleEl;
    _closeProactivePanel();
    runSectionLoaders(sectionName);
    setRouteHash(sectionName);
}

registerShowSection(showSection);

($$('.nav-links a') as NodeListOf<HTMLAnchorElement>).forEach(a => {
    a.addEventListener('click', async e => {
        // 2026-07-29 (raport Dan „Transmisii live nu face nimic"): handler-ul
        // înghițea click-ul pe ORICE link din meniu. Linkurile REALE (href
        // adevărat, fără data-section — ex. /live/monitor) se lasă browserului.
        if (!a.dataset.section && a.getAttribute('href') && a.getAttribute('href') !== '#') {
            return;
        }
        e.preventDefault();
        const section = a.dataset.section;
        const overlay = document.getElementById('revision-detail-overlay');
        if (overlay && !overlay.classList.contains('hidden') && _wfWorkflowId) {
            var ss = window._wfGetStepsState ? window._wfGetStepsState() : {total:0,done:0};
            if (ss.total > 0 && ss.done === 0) {
                // zero pași bifați = doar s-a uitat — anulăm silențios sesiunea
                // (altfel rămâne workflow fantomă care ascunde intervalele mașinii)
                _clearWorkflowSession(true);
            } else if (ss.total > 0 && ss.done < ss.total) {
                const choice = await askChoice('Mentenanță în desfășurare',
                    `${ss.done}/${ss.total} pași finalizați. Ce faci cu lucrarea?`, [
                    { key: 'pauza', label: '⏸ Pauză — salvează progresul (reiei din Calendar)' },
                    { key: 'raman', label: '← Rămân în lucrare' },
                    { key: 'anuleaza', label: '✕ Anulează definitiv lucrarea', danger: true },
                ]);
                if (choice === null || choice === 'raman') return;
                if (choice === 'anuleaza') {
                    _clearWorkflowSession(true);
                } else {
                    window._wfSaveProgress('Progres salvat. Poți relua din Calendar.');
                }
            }
        }
        $$('.nav-links a').forEach(x => x.classList.remove('active'));
        a.classList.add('active');
        const secEl = $(`#sec-${section}`);
        const altaSectiune = !!secEl && !secEl.classList.contains('active');
        $$('.section').forEach(s => s.classList.remove('active'));
        if (secEl) secEl.classList.add('active');
        // secțiunea nouă se deschide de sus (13 oct 2026, plimbarea de audit): pagina se derulează în fereastră, iar
        // pe telefon „Piese” se deschidea la înălțimea Jurnalului derulat — filele rămâneau sub bara fixă de sus
        if (altaSectiune) window.scrollTo(0, 0);
        $('#page-title')!.textContent = a.textContent!.trim();
        // Închide sertarul doar pe tabletă/mobil (CSS: sidebar overlay sub 1200px), nu pe desktop unde bara rămâne vizibilă
        if (typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 1199px)').matches) {
            _closeSidebar();
        }
        _closeProactivePanel();
        runSectionLoaders(section);
        setRouteHash(section || 'dashboard');
        _showActiveInterventionBanner();
    });
});

function _closeSidebar(): void {
    $('#sidebar')!.classList.remove('open');
    const bd = document.getElementById('sidebar-backdrop');
    if (bd) bd.classList.remove('visible');
}
function _openSidebar(): void {
    $('#sidebar')!.classList.add('open');
    const bd = document.getElementById('sidebar-backdrop');
    if (bd) bd.classList.add('visible');
}
$('#menu-toggle')!.addEventListener('click', () => {
    const sb = $('#sidebar')!;
    if (sb.classList.contains('open')) _closeSidebar();
    else _openSidebar();
});
const _bd = document.getElementById('sidebar-backdrop');
if (_bd) _bd.addEventListener('click', _closeSidebar);
document.getElementById('sidebar-close')?.addEventListener('click', _closeSidebar);

/* ═══ SIDEBAR GROUP TOGGLE + USERS LINK ═══════════════════════ */
// Cifra de lângă numele grupului se NUMĂRĂ. Era scrisă de mână („7") și grupul
// Inginer avea 9 iteme (audit 20 sep 2026) — se decalase la fiecare meniu nou.
document.querySelectorAll('.nav-group').forEach(group => {
    const nr = group.querySelector('.group-count');
    if (nr) nr.textContent = String(group.querySelectorAll('.nav-submenu .nav-item').length);
});
document.querySelectorAll('.nav-group-header').forEach(header => {
    header.addEventListener('click', () => {
        const group = header.closest('.nav-group');
        if (group) group.classList.toggle('open');
    });
});

const usersLink = document.getElementById('nav-users-link');
if (usersLink) {
    usersLink.addEventListener('click', () => {
        // Delegate to Hub admin UI (Strategy A+B, CONTRACT v1.5 #28).
        // See _openHubAdminUsers() in auth-ui.ts for full rationale.
        const open = (window as unknown as { _openHubAdminUsers?: () => void })._openHubAdminUsers;
        if (typeof open === 'function') open();
    });
}

/* ═══ SCHEDULE INTERVENTION ════════════════════════════════════ */
let _siInitialized = false;
async function initScheduleIntervention(): Promise<void> {
    if (_siInitialized) { _loadRecentTasks(); return; }
    _siInitialized = true;

    await _ensureMachineCache();
    const lineSel = document.getElementById('si-line') as HTMLSelectElement;
    const machineSel = document.getElementById('si-machine') as HTMLSelectElement;
    const assigneeSel = document.getElementById('si-assignee') as HTMLSelectElement;
    const dueDateEl = document.getElementById('si-due-date') as HTMLInputElement;
    if (!lineSel || !machineSel) return;

    _populateLineSelect(lineSel, '— Selectează linia —');
    _linkLineToMachineSelect(lineSel, machineSel, '— Selectează utilajul —', true);

    if (dueDateEl) {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        // 🌙 Ziua LOCALĂ, nu UTC (audit 2026-08-19, constatarea 2.7): cu `toISOString()`,
        // între 00:00 și 03:00 ora locală „mâine" ieșea de fapt ziua curentă, iar lucrarea
        // programată în tura de noapte se năștea scadentă chiar azi.
        dueDateEl.value = localDateKey(tomorrow);
    }

    try {
        const usernames = await api('/api/auth/usernames');
        assigneeSel.innerHTML = '<option value="">— Selectează mecanic —</option>';
        (usernames || []).forEach((u: string) => {
            assigneeSel.innerHTML += `<option value="${escHtml(u)}">${escHtml(u)}</option>`;
        });
    } catch (_e) {
        assigneeSel.innerHTML = '<option value="">Eroare la încărcare</option>';
    }

    document.getElementById('si-submit-btn')?.addEventListener('click', async () => {
        const machineId = machineSel.value;
        const assignee = assigneeSel.value;
        const description = (document.getElementById('si-description') as HTMLTextAreaElement)?.value?.trim();
        const dueDate = dueDateEl?.value;
        const priority = (document.getElementById('si-priority') as HTMLSelectElement)?.value || 'normal';
        const notes = (document.getElementById('si-notes') as HTMLTextAreaElement)?.value?.trim();

        if (!machineId) { toast('Selectează mașina!', 'error'); return; }
        if (!description) { toast('Completează descrierea lucrării!', 'error'); return; }
        if (!assignee) { toast('Selectează mecanicul!', 'error'); return; }

        const lineId = lineSel.value || '';
        const assignedBy = state.currentUser?.username || 'inginer';

        const btn = document.getElementById('si-submit-btn') as HTMLButtonElement;
        btn.style.opacity = '0.5';
        btn.style.pointerEvents = 'none';

        try {
            const resp = await fetch('/api/operator_tasks', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    machine_id: machineId,
                    assigned_to: assignee,
                    assigned_by: assignedBy,
                    due_date: dueDate || todayStr(),
                    description: description,
                    task_type: 'assigned',
                    line_id: lineId,
                    priority: priority,
                    notes: notes || '',
                })
            });
            const result = await resp.json();
            if (result.error) throw new Error(result.error);

            toast(`Intervenție programată pentru ${assignee}!`);
            const resultDiv = document.getElementById('si-result');
            if (resultDiv) {
                resultDiv.innerHTML = `<div style="padding:10px;background:rgba(0,230,118,0.08);border:1px solid var(--green);border-radius:8px;color:var(--green)">
                    ✅ Intervenție #${result.id} creată — atribuită lui <strong>${escHtml(assignee)}</strong> pentru ${escHtml(dueDate || 'azi')}
                </div>`;
            }

            (document.getElementById('si-description') as HTMLTextAreaElement).value = '';
            (document.getElementById('si-notes') as HTMLTextAreaElement).value = '';
            _loadRecentTasks();
        } catch (e: unknown) {
            toast('Eroare: ' + (e instanceof Error ? e.message : String(e)), 'error');
        } finally {
            btn.style.opacity = '';
            btn.style.pointerEvents = '';
        }
    });

    _loadRecentTasks();
}

async function _loadRecentTasks(): Promise<void> {
    const container = document.getElementById('si-recent-list');
    if (!container) return;
    try {
        // 30 sep 2026: lista lua primele 10 după scadență (cele mai VECHI) și sărea peste cele
        // amânate / în pauză — intervenția abia creată putea să nu apară deloc aici
        const toate = await api('/api/operator_tasks?status=pending,assigned,in_progress,paused,postponed,waiting_parts');
        const tasks = (toate || []).slice().sort((a: any, b: any) => (b.id || 0) - (a.id || 0));
        if (!tasks || tasks.length === 0) {
            container.innerHTML = '<p style="color:var(--text-muted)">Nu există intervenții programate active.</p>';
            return;
        }
        const STARE: Record<string, string> = {
            pending: 'de făcut', assigned: 'alocată', in_progress: 'în lucru', paused: 'în pauză',
            postponed: 'amânată', waiting_parts: 'lipsă piesă',
        };
        let html = '<div style="display:flex;flex-direction:column;gap:6px">';
        tasks.slice(0, 15).forEach((t: any) => {
            // prioritate din coloană; task-urile vechi o aveau ca etichetă [high] în note
            const isHigh = t.priority === 'high' || (t.notes || '').includes('[high]');
            const priorityBadge = isHigh ? '<span class="badge badge-red" style="font-size:.7rem">URGENT</span>' : '';
            html += `<div style="padding:8px 12px;background:var(--surface);border:1px solid var(--border);border-radius:8px;display:flex;align-items:center;gap:10px;font-size:.85rem;flex-wrap:wrap">
                <span style="color:var(--cyan);font-weight:600;min-width:70px">${escHtml(t.machine_id || '')}</span>
                <span style="flex:1;min-width:140px">${escHtml(t.description || 'Fără descriere')}</span>
                <span style="color:var(--text-muted);font-size:.8rem">${escHtml(t.assigned_to || '')}</span>
                <span style="color:var(--text-muted);font-size:.8rem">${escHtml(t.due_date || '')}</span>
                ${priorityBadge}
                <span class="badge badge-yellow" style="font-size:.7rem">${escHtml(STARE[t.status] || t.status || 'de făcut')}</span>
                <button type="button" class="btn btn-xs si-task-move" data-task-id="${t.id}" data-due="${escHtml(t.due_date || '')}" style="padding:4px 10px" title="Reprogramează">📅</button>
                <button type="button" class="btn btn-xs si-task-cancel" data-task-id="${t.id}" style="padding:4px 10px;color:#ef5350;border-color:rgba(239,83,80,.4)" title="Anulează intervenția">✕</button>
            </div>`;
        });
        html += '</div>';
        container.innerHTML = html;

        container.querySelectorAll('.si-task-move').forEach(btn => {
            btn.addEventListener('click', async () => {
                const id = (btn as HTMLElement).dataset.taskId;
                const nd = await askText('Reprogramează intervenția', {
                    label: 'Noua dată (AAAA-LL-ZZ):', value: (btn as HTMLElement).dataset.due || '',
                    required: true, okText: 'Mută',
                });
                if (nd === null) return;
                if (!/^\d{4}-\d{2}-\d{2}$/.test(nd.trim())) { toast('Dată invalidă — format AAAA-LL-ZZ', 'warning'); return; }
                try {
                    await api('/api/operator_tasks/' + id, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ due_date: nd.trim() }),
                    });
                    toast('Intervenție reprogramată', 'success');
                    _loadRecentTasks();
                } catch (e: any) { toast('Eroare: ' + (e?.message || e), 'error'); }
            });
        });
        container.querySelectorAll('.si-task-cancel').forEach(btn => {
            btn.addEventListener('click', async () => {
                const id = (btn as HTMLElement).dataset.taskId;
                if (!await askConfirm('Anulezi intervenția programată?', 'Rămâne în istoric cu status «cancelled» — nu se șterge.')) return;
                try {
                    await api('/api/operator_tasks/' + id, {
                        method: 'PUT', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ status: 'cancelled' }),
                    });
                    toast('Intervenție anulată', 'info');
                    _loadRecentTasks();
                } catch (e: any) { toast('Eroare: ' + (e?.message || e), 'error'); }
            });
        });
    } catch (_e) {
        container.innerHTML = '<p style="color:var(--red)">Eroare la încărcare.</p>';
    }
}

/* ═══ Global bindings for inline HTML handlers (quality module) ═══ */
window._pqUpload = _pqUpload;
window._pqDeleteDoc = _pqDeleteDoc;
window._pqDeleteTemplate = _pqDeleteTemplate;
window._pqSubmitCheck = _pqSubmitCheck;
window._pqCreateTemplate = _pqCreateTemplate;
window._pqAddField = _pqAddField;
window._pqCheckValue = _pqCheckValue;

/* ═══ INIT ═════════════════════════════════════════════════════ */
Object.entries(state.machineHours || {}).forEach(([mid, hrs]: [string, any]) => {
    if (hrs > 0) recordHoursHistory(mid, hrs);
});

async function updateMyTasksBadge(): Promise<void> {
    try {
        /* Insigna arăta ZERO cu 128 de restanțe și 48 de lucrări azi (audit 20 sep
         * 2026): `badge-count` număra doar task-urile MANUALE alocate, nu planul
         * din catalog. Meniul spunea „liniște" exact când era cel mai mult de
         * făcut. Acum = Azi + Restante, aceleași cifre ca ecranul. */
        const data = await api('/api/mytasks/badges');
        const badge = document.getElementById('mytasks-badge');
        if (!badge) return;
        const count = (data?.today || 0) + (data?.overdue || 0);
        badge.title = `${data?.today || 0} azi · ${data?.overdue || 0} restante`;
        dBaraJosLucrari(count);   // aceeași cifră pe bara de jos a temei D
        if (count > 0) {
            badge.textContent = String(count);
            badge.style.display = '';
        } else {
            badge.style.display = 'none';
        }
    } catch (_e) { /* silent */ }
}

async function initApp(): Promise<void> {
    _initMaintHistoryBack();
    initManualLaPagina();   // „Manual p.N" pe telefon: vizualizatorul aplicației, la pagina N (29 sep 2026)

    await loadCurrentUser();

    if (!state.currentUser?.username) {
        return;
    }

    const config = await loadFactoryConfig();
    applyFactoryTheme(config);

    applyInitialAppRoute();
    _deschideDinLegatura();

    const role = (state.currentUser?.role) || 'operator';
    if (role === 'master' || role === 'engineer') {
        updateValidationBadge();
        setInterval(updateValidationBadge, 60_000);
        // Butonul mare de alarme + alarmele scrise de aplicație (19 sep 2026).
        // Inginerul le vede pe cele despre utilaje; masterul le vede și pe cele
        // ale aplicației — ruta decide, nu ecranul.
        void incarcaIncidenteAplicatie().then(actualizeazaButonAlarme);
        setInterval(() => { void incarcaIncidenteAplicatie().then(actualizeazaButonAlarme); }, 120_000);
    }
    actualizeazaButonAlarme();
    if (role === 'master') {
        // incidentele de sistem sunt vizibile doar masterului, ca și pagina lor
        void actualizeazaInsignaIncidente();
        setInterval(() => { void actualizeazaInsignaIncidente(); }, 300_000);
    }
    updateMyTasksBadge();
    setInterval(updateMyTasksBadge, 60_000);

    // Part 3 (2026-05-15) — indicator GO-LIVE în topbar (refresh la 30s în overview.ts)
    loadGoliveStatus();

    window.addEventListener('hashchange', () => {
        if (!state.currentUser?.username) return;
        const p = parseRouteHash();
        if (!p) return;
        if (p.kind === 'hub') {
            openSpatialNavDemo();
            return;
        }
        const el = document.getElementById(`sec-${p.name}`);
        if (el) showSection(`sec-${p.name}`);
    });

    initSpatialNavDemo();

    // In HUB embed mode the parent (hub.cpp-romania.com) owns auth/session lifecycle:
    // we MUST NOT redirect away on logout/idle (that breaks the iframe). Hub will
    // either re-issue SSO cookie or unmount the iframe via postMessage('logout').
    const inHubEmbed = !!(window as any).__EMBED_HUB__;

    // Logout button (with 2-step confirmation, sidebar) + idle auto-logout for masters.
    if (!inHubEmbed) {
        wireLogout();
        if (role === 'master') {
            /* Durata se poate scurta din probe (`window.__IDLE_MS__`), altfel un
               test al ceasului de inactivitate ar dura o oră. În producție nu
               există variabila, deci rămâne 1h. */
            wireIdleTimeout(Number((window as any).__IDLE_MS__) || 60 * 60 * 1000);
        }
    }
    // HUB cross-module bus: react to logout broadcast from parent hub.
    wireHubBus();
}

/** Legăturile din alertele Hub (`/machine/<cod>`, `/workflow/<id>` → `/?deschide_utilaj=…`):
 *  deschide panoul utilajului sau fișa lucrării, apoi scoate parametrul din adresă (3 oct 2026). */
function _deschideDinLegatura(): void {
    const qs = new URLSearchParams(location.search);
    const utilaj = qs.get('deschide_utilaj');
    const lucrare = qs.get('deschide_lucrare');
    if (!utilaj && !lucrare) return;
    qs.delete('deschide_utilaj');
    qs.delete('deschide_lucrare');
    const rest = qs.toString();
    history.replaceState(history.state, '', location.pathname + (rest ? '?' + rest : '') + location.hash);
    setTimeout(() => {
        if (utilaj) (window as any).openMachinePanel?.(utilaj);
        else if (lucrare && /^\d+$/.test(lucrare)) void openWorkflowDetail(lucrare);
    }, 400);
}

/**
 * Listen for postMessage from hub.cpp-romania.com (parent window when we run in iframe).
 *
 * Protocol (defined in cappetplast-platform/docs/HUB_SSO_SPEC.md):
 *   - { type: 'logout' }         → hub user logged out, clear our session too
 *   - { type: 'navigate_home' }  → hub asked us to return to dashboard
 *
 * Strict origin check — we only trust messages from the hub's domain.
 */
function wireHubBus(): void {
    const HUB_ORIGIN = 'https://hub.cpp-romania.com';
    window.addEventListener('message', async (e: MessageEvent) => {
        if (e.origin !== HUB_ORIGIN) return; // hard origin gate
        const msg: any = e.data || {};
        if (!msg || typeof msg !== 'object') return;
        if (msg.type === 'logout') {
            try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }); } catch (_) { /* ignore */ }
            try { localStorage.clear(); sessionStorage.clear(); } catch (_) { /* ignore */ }
            // No redirect — hub will unmount the iframe. We just blank the UI.
            try { document.body.innerHTML = ''; } catch (_) { /* ignore */ }
        } else if (msg.type === 'navigate_home') {
            // link-ul „Transmisii live" respectă prefixul instalării (hub: /maint)
            const lm = document.getElementById('nav-live-monitor') as HTMLAnchorElement | null;
            if (lm) {
                const pref = location.pathname.replace(new RegExp('/(index\\.html)?$'), '');
                if (pref) lm.href = pref + '/live/monitor';
            }
            const dash = document.querySelector<HTMLElement>('.nav-item[data-section="dashboard"]');
            if (dash) dash.click();
        }
    });
}

/**
 * Wires the sidebar "IEȘI DIN CONT" button:
 * - First click: shows confirmation panel
 * - "Anulează" → hides panel
 * - "DA, IEȘI" → POST /api/auth/logout, then redirect to login
 */
function wireLogout(): void {
    const btn = document.getElementById('btnLogout');
    const confirm = document.getElementById('logoutConfirm');
    const cancel = document.getElementById('btnLogoutCancel');
    const yes = document.getElementById('btnLogoutYes');
    if (!btn || !confirm || !cancel || !yes) return;

    const close = () => { confirm.hidden = true; document.removeEventListener('keydown', onKey); };
    const open = () => {
        confirm.hidden = false;
        document.addEventListener('keydown', onKey);
        // focus the cancel button by default to avoid accidental logout on Enter
        setTimeout(() => (cancel as HTMLButtonElement).focus(), 50);
    };
    const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') { e.preventDefault(); close(); }
    };

    btn.addEventListener('click', (e) => { e.stopPropagation(); open(); });
    cancel.addEventListener('click', close);
    // close on backdrop click
    const backdrop = confirm.querySelector('.logout-modal-backdrop');
    if (backdrop) backdrop.addEventListener('click', close);

    yes.addEventListener('click', async () => {
        (yes as HTMLButtonElement).disabled = true;
        yes.textContent = 'Se deconectează...';
        try {
            await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' });
        } catch (_) { /* network error — proceed anyway */ }
        try { localStorage.clear(); sessionStorage.clear(); } catch (_) { /* ignore */ }
        window.location.href = '/?logout=1';
    });
}

/**
 * Auto-logout after `ms` milliseconds of user inactivity.
 * Resets on mouse, keyboard, scroll, touch and document visibility changes.
 * Used only for elevated roles (master) — operators stay logged in for the
 * full 8h session lifetime.
 */
function wireIdleTimeout(ms: number): void {
    let timer: number | undefined;
    let warnTimer: number | undefined;
    let warningShown = false;
    // Urmă pentru probe: „ceasul chiar s-a armat, pe atâtea milisecunde".
    // Fără ea, un test care nu vede avertismentul nu poate deosebi «ceasul e
    // reparat» de «ceasul nici n-a pornit» — exact confuzia din 5 sep 2026.
    (window as any).__idleWired__ = ms;

    function doLogout(reason: string) {
        try { localStorage.clear(); sessionStorage.clear(); } catch (_) { /* ignore */ }
        // Best-effort server-side logout; redirect always happens.
        fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' })
            .catch(() => undefined)
            .finally(() => { window.location.href = `/?idle=${encodeURIComponent(reason)}`; });
    }

    function showWarning() {
        if (warningShown) return;
        warningShown = true;
        if (typeof toast === 'function') {
            toast('⚠ Vei fi deconectat automat în 1 minut din cauza inactivității.', 'warning' as any);
        }
    }

    function reset() {
        warningShown = false;
        if (timer) window.clearTimeout(timer);
        /* 5 sep 2026 (Dan: «de ce îmi tot apare că voi fi deconectat, dar eu
           navighez în aplicație?»): se anula DOAR cronometrul de deconectare.
           Cel de avertizare se programa din nou la fiecare mișcare de mouse,
           tastă, clic sau derulare — și nu-l oprea nimeni. După prima oră cu
           pagina deschisă, avertismentele lăsate în urmă începeau să sosească
           unul după altul, iar `warningShown` nu le oprea fiindcă orice
           activitate îl cobora înapoi pe false. Adică exact omul care lucra
           primea mesajul «ești inactiv», iar cel care chiar stătea degeaba îl
           primea o singură dată. Pe deasupra, rămânea câte un cronometru viu
           pentru fiecare eveniment de mouse, 59 de minute fiecare. */
        if (warnTimer) window.clearTimeout(warnTimer);
        // Warning 60s before logout
        const warnAt = Math.max(ms - 60_000, ms / 2);
        warnTimer = window.setTimeout(showWarning, warnAt);
        timer = window.setTimeout(() => doLogout('inactivitate'), ms);
    }

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((ev) => document.addEventListener(ev, reset, { passive: true }));
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') reset();
    });
    reset();
}

function boot(): void {
    initAtmosphereCanvas();
    void initApp();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
